import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BadgeCheck, Eye, FileText, Loader2, X } from 'lucide-react';
import {
  DOCUMENT_TYPE_LABELS,
  EMPLOYMENT_STATUS_LABELS,
  formatLongDate,
  formatShortDate,
} from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Table,
  Td,
  Textarea,
  Th,
  type PageMeta,
} from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { KYC_STATUS } from '@/lib/status';

interface QueueRow {
  id: string;
  memberId: string;
  memberName: string;
  membershipNumber: string;
  email: string;
  status: string;
  submittedAt: string | null;
  documentCount: number;
}

interface ReviewProfile {
  status: string;
  fullLegalName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  residentialAddress: string | null;
  city: string | null;
  state: string | null;
  lga: string | null;
  employmentStatus: string | null;
  organisation: string | null;
  jobTitle: string | null;
  monthlyIncomeBand: string | null;
  nextOfKin: {
    fullName: string;
    relationship: string;
    phone: string;
    email: string | null;
    address: string;
  } | null;
  identificationType: string | null;
  identificationNumberMasked: string | null;
  submittedAt: string | null;
  member: {
    id: string;
    name: string;
    membershipNumber: string;
    email: string;
    phone: string;
    memberSince: string;
    status: string;
  };
  bankAccounts: Array<{
    bankName: string;
    accountNumber: string;
    accountName: string;
    verifiedAt: string | null;
  }>;
  documents: Array<{
    id: string;
    documentType: string;
    fileName: string;
    mimeType: string;
    fileSize: number;
    status: string;
    uploadedAt: string;
  }>;
  history: Array<{
    decision: string;
    previousStatus: string;
    newStatus: string;
    reason: string | null;
    reviewer: string;
    at: string;
  }>;
}

const CORRECTABLE_FIELDS = [
  'fullLegalName',
  'dateOfBirth',
  'residentialAddress',
  'state',
  'lga',
  'employmentStatus',
  'nextOfKin',
  'bankDetails',
  'identificationNumber',
  'GOVERNMENT_ID',
  'PASSPORT_PHOTOGRAPH',
];

export default function AdminKycReview() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['kyc-queue', page, status],
    queryFn: () =>
      api.get<{ data: QueueRow[]; meta: PageMeta }>(
        `/kyc?pageSize=25&page=${page}${status ? `&status=${status}` : ''}`,
      ),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Verification"
        title="KYC review"
        description={data ? `${data.meta.total} awaiting attention` : undefined}
        actions={
          <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="w-56">
            <option value="">Awaiting review</option>
            {Object.entries(KYC_STATUS).map(([key, presentation]) => (
              <option key={key} value={key}>
                {presentation.label}
              </option>
            ))}
          </Select>
        }
      />

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !data || data.data.length === 0 ? (
        <EmptyState
          icon={<BadgeCheck size={20} />}
          title="Nothing to review"
          description="Submitted KYC profiles appear here for verification."
        />
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <thead>
                <tr>
                  <Th>Membership</Th>
                  <Th>Member</Th>
                  <Th>Email</Th>
                  <Th>Status</Th>
                  <Th numeric>Documents</Th>
                  <Th>Submitted</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.data.map((row) => (
                  <tr key={row.id} className="transition-colors hover:bg-muted/50">
                    <Td className="numeric font-semibold">{row.membershipNumber}</Td>
                    <Td className="font-medium">{row.memberName}</Td>
                    <Td className="text-foreground-secondary">{row.email}</Td>
                    <Td>
                      <StatusBadge map={KYC_STATUS} status={row.status} />
                    </Td>
                    <Td numeric>{row.documentCount}</Td>
                    <Td className="numeric text-foreground-secondary">
                      {row.submittedAt ? formatShortDate(new Date(row.submittedAt)) : '—'}
                    </Td>
                    <Td>
                      <Button size="sm" variant="outline" onClick={() => setSelectedMemberId(row.memberId)}>
                        Review
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination meta={data.meta} onPageChange={setPage} />
          </CardContent>
        </Card>
      )}

      {selectedMemberId && (
        <ReviewPanel memberId={selectedMemberId} onClose={() => setSelectedMemberId(null)} />
      )}
    </div>
  );
}

function ReviewPanel({ memberId, onClose }: { memberId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [corrections, setCorrections] = useState<string[]>([]);
  const [openingDocument, setOpeningDocument] = useState<string | null>(null);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['kyc-review', memberId],
    queryFn: () => api.get<ReviewProfile>(`/kyc/${memberId}`),
  });

  const beginReview = useMutation({
    mutationFn: () => api.post(`/kyc/${memberId}/begin-review`),
    onSuccess: () => {
      toast.success('Taken into review.');
      void queryClient.invalidateQueries({ queryKey: ['kyc-review', memberId] });
      void queryClient.invalidateQueries({ queryKey: ['kyc-queue'] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed.'),
  });

  const decide = useMutation({
    mutationFn: (decision: 'APPROVE' | 'REJECT' | 'REQUEST_CORRECTION') =>
      api.post<{ message: string }>(`/kyc/${memberId}/review`, {
        decision,
        ...(reason ? { reason } : {}),
        ...(notes ? { internalNotes: notes } : {}),
        ...(corrections.length ? { fieldsRequiringCorrection: corrections } : {}),
      }),
    onSuccess: (result) => {
      toast.success(result.message);
      void queryClient.invalidateQueries({ queryKey: ['kyc-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
      onClose();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  /**
   * Opens a document through a short-lived signed URL.
   *
   * The API records who viewed whose identity document — these are the most
   * sensitive objects in the system, so the access itself is the audit event.
   */
  const openDocument = async (documentId: string) => {
    setOpeningDocument(documentId);
    try {
      const { url } = await api.get<{ url: string }>(`/kyc/documents/${documentId}/url`);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not open document.');
    } finally {
      setOpeningDocument(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/50 p-0 sm:items-center sm:p-6">
      <Card className="max-h-[92vh] w-full max-w-3xl overflow-y-auto">
        {isLoading || !profile ? (
          <CardContent className="p-8">
            <Skeleton className="h-64" />
          </CardContent>
        ) : (
          <>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle>{profile.member.name}</CardTitle>
                  <p className="numeric mt-1 text-sm text-foreground-secondary">
                    {profile.member.membershipNumber} · member since{' '}
                    {formatShortDate(new Date(profile.member.memberSince))}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge map={KYC_STATUS} status={profile.status} />
                  <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
                    <X size={18} />
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              {profile.status === 'SUBMITTED' && (
                <Button size="sm" variant="outline" loading={beginReview.isPending} onClick={() => beginReview.mutate()}>
                  Take into review
                </Button>
              )}

              <Section title="Identity">
                <Item label="Full legal name" value={profile.fullLegalName} />
                <Item
                  label="Date of birth"
                  value={profile.dateOfBirth ? formatLongDate(new Date(profile.dateOfBirth)) : null}
                />
                <Item label="Gender" value={profile.gender} />
                <Item label="ID type" value={profile.identificationType?.replace(/_/g, ' ')} />
                {/* Masked by default. The full number is in the uploaded
                    document, and opening that is itself audited. */}
                <Item label="ID number" value={profile.identificationNumberMasked} />
              </Section>

              <Section title="Address">
                <Item label="Residential address" value={profile.residentialAddress} />
                <Item label="City" value={profile.city} />
                <Item label="State" value={profile.state} />
                <Item label="LGA" value={profile.lga} />
              </Section>

              <Section title="Employment">
                <Item
                  label="Status"
                  value={
                    profile.employmentStatus
                      ? EMPLOYMENT_STATUS_LABELS[profile.employmentStatus]
                      : null
                  }
                />
                <Item label="Organisation" value={profile.organisation} />
                <Item label="Job title" value={profile.jobTitle} />
                <Item label="Income band" value={profile.monthlyIncomeBand} />
              </Section>

              <Section title="Next of kin">
                <Item label="Name" value={profile.nextOfKin?.fullName} />
                <Item label="Relationship" value={profile.nextOfKin?.relationship} />
                <Item label="Phone" value={profile.nextOfKin?.phone} />
                <Item label="Address" value={profile.nextOfKin?.address} />
              </Section>

              <Section title="Bank account">
                {profile.bankAccounts.length === 0 ? (
                  <p className="col-span-2 text-sm text-foreground-secondary">
                    No bank account on file — a loan could not be disbursed.
                  </p>
                ) : (
                  profile.bankAccounts.map((account) => (
                    <div key={account.accountNumber} className="col-span-2">
                      <p className="numeric text-sm font-semibold text-foreground">
                        {account.accountNumber} · {account.bankName}
                      </p>
                      <p className="text-xs text-foreground-secondary">{account.accountName}</p>
                    </div>
                  ))
                )}
              </Section>

              {/* Documents */}
              <div>
                <p className="eyebrow mb-3">Documents</p>
                <div className="space-y-2">
                  {profile.documents.length === 0 && (
                    <p className="text-sm text-foreground-secondary">No documents uploaded.</p>
                  )}
                  {profile.documents.map((document) => (
                    <div
                      key={document.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <FileText size={16} className="shrink-0 text-foreground-secondary" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-foreground">
                            {DOCUMENT_TYPE_LABELS[document.documentType] ?? document.documentType}
                          </p>
                          <p className="truncate text-xs text-foreground-secondary">
                            {document.fileName} · {(document.fileSize / 1024).toFixed(0)} KB
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        loading={openingDocument === document.id}
                        onClick={() => void openDocument(document.id)}
                      >
                        {openingDocument === document.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Eye size={14} />
                        )}
                        View
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-foreground-secondary">
                  Opening a document is recorded against your account in the audit trail.
                </p>
              </div>

              {profile.history.length > 0 && (
                <div>
                  <p className="eyebrow mb-3">Review history</p>
                  <div className="space-y-2">
                    {profile.history.map((entry, index) => (
                      <div key={index} className="rounded-lg bg-muted p-3 text-sm">
                        <p className="font-semibold text-foreground">
                          {entry.reviewer} · {entry.previousStatus} → {entry.newStatus}
                        </p>
                        {entry.reason && (
                          <p className="mt-1 text-foreground-secondary">{entry.reason}</p>
                        )}
                        <p className="mt-1 text-xs text-foreground-secondary">
                          {formatLongDate(new Date(entry.at))}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Decision */}
              <div className="space-y-4 border-t border-border pt-5">
                <Field
                  label="Reason / message to the member"
                  htmlFor="reason"
                  hint="Required when rejecting or requesting corrections. The member sees this."
                >
                  <Textarea id="reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                </Field>

                <div>
                  <p className="mb-2 text-sm font-semibold text-foreground">
                    Fields needing correction
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {CORRECTABLE_FIELDS.map((field) => {
                      const active = corrections.includes(field);
                      return (
                        <button
                          key={field}
                          type="button"
                          onClick={() =>
                            setCorrections((current) =>
                              active ? current.filter((f) => f !== field) : [...current, field],
                            )
                          }
                          className={
                            active
                              ? 'rounded-full bg-warning px-3 py-1 text-xs font-semibold text-warning-foreground'
                              : 'rounded-full border border-border px-3 py-1 text-xs font-semibold text-foreground-secondary hover:bg-accent'
                          }
                        >
                          {humanise(field)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <Field label="Internal notes" htmlFor="notes" hint="Never shown to the member.">
                  <Textarea id="notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </Field>

                <Alert tone="info">
                  Approving KYC activates the member and unlocks loan applications.
                </Alert>

                <div className="flex flex-wrap gap-3">
                  <Button variant="success" loading={decide.isPending} onClick={() => decide.mutate('APPROVE')}>
                    <BadgeCheck size={16} /> Approve
                  </Button>
                  <Button
                    variant="outline"
                    disabled={!reason.trim()}
                    loading={decide.isPending}
                    onClick={() => decide.mutate('REQUEST_CORRECTION')}
                  >
                    Request corrections
                  </Button>
                  <Button
                    variant="danger"
                    disabled={!reason.trim()}
                    loading={decide.isPending}
                    onClick={() => decide.mutate('REJECT')}
                  >
                    <X size={16} /> Reject
                  </Button>
                </div>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="eyebrow mb-3">{title}</p>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Item({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-foreground-secondary">{label}</p>
      <p className="text-sm text-foreground">{value || '—'}</p>
    </div>
  );
}

function humanise(value: string): string {
  if (DOCUMENT_TYPE_LABELS[value]) return DOCUMENT_TYPE_LABELS[value]!;
  const text = value.replace(/[._]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}
